/**
 * Contas de desempenho dos modelos: taxas, variacao contra o periodo
 * anterior, somas e o ranking "melhor da categoria".
 *
 * Decisoes nao obvias:
 *  1. Toda taxa sai em bps INTEIROS (razao de duas contagens, uma divisao so)
 *     e vira texto em lib/format. Denominador zero devolve `null`: "0%" de
 *     nada e uma afirmacao falsa sobre o modelo.
 *  2. A taxa que decide o ranking e PAGARAM / RECEBERAM, e nao cliques ou
 *     respostas: e a unica que vira comissao. Um modelo que gera muita
 *     resposta e pouca venda nao e "o melhor" -- vira insight separado.
 *  3. "Categoria" = mesmo canal + mesma categoria da Meta + mesmo publico +
 *     mesmo momento (ex.: Utility · WhatsApp oficial · Carrinho abandonado
 *     T+1h). E o que um teste A/B compara. O T+1h fala com quem acabou de
 *     sair; o T+48h, com quem ja ignorou dois toques -- juntar os dois
 *     premiaria o momento, nao o texto.
 *  4. So entra no ranking modelo que de fato saiu (aprovado ou texto livre),
 *     com amostra minima (50 envios na janela: 2 de 3 nao e 66%, e sorte) e de
 *     publico que GERA credito. Mensagem da vendedora e pos-venda nunca
 *     creditam pagamento, entao nao competem pela taxa. Grupo com um modelo so
 *     nao tem "melhor".
 *  5. A soma de `paid` entre modelos NAO conta a mesma venda duas vezes:
 *     cada pagamento e creditado a um contato so, o ultimo (ADR-001).
 */

import type { MessageTemplate, TemplatePerformance } from './types';
import { CANAL_CURTO, CATEGORIA_LABEL, PUBLICO_LABEL } from './vocabulario';

export const AMOSTRA_MINIMA = 50;

export const DESEMPENHO_ZERO: TemplatePerformance = {
  sent: 0,
  delivered: 0,
  opened: null,
  clicked: null,
  replied: 0,
  paid: 0,
  paidViaSeller: 0,
  recoveredCents: 0,
};

/** Razao inteira em bps (1500 = 15,00%); `null` com denominador zero. */
export function taxaBps(parte: number, todo: number): number | null {
  return todo <= 0 ? null : Math.round((parte * 10_000) / todo);
}

export function taxaPagamentoBps(p: TemplatePerformance): number | null {
  return taxaBps(p.paid, p.sent);
}

export function taxaRespostaBps(p: TemplatePerformance): number | null {
  return taxaBps(p.replied, p.delivered);
}

export function taxaCliqueBps(p: TemplatePerformance): number | null {
  return p.clicked === null ? null : taxaBps(p.clicked, p.delivered);
}

// -----------------------------------------------------------------------------
// Variacao
// -----------------------------------------------------------------------------

export type Direcao = 'alta' | 'queda' | 'igual' | 'sem_base';

export interface Variacao {
  readonly direcao: Direcao;
  readonly absoluta: number;
  /** Variacao relativa em bps; `null` quando o anterior era zero (nao ha base). */
  readonly relativaBps: number | null;
}

export function variar(atual: number, anterior: number | null): Variacao {
  if (anterior === null) {
    return { direcao: 'sem_base', absoluta: 0, relativaBps: null };
  }
  const absoluta = atual - anterior;
  if (absoluta === 0) {
    return { direcao: 'igual', absoluta: 0, relativaBps: 0 };
  }
  return {
    direcao: absoluta > 0 ? 'alta' : 'queda',
    absoluta,
    relativaBps: anterior === 0 ? null : Math.round((absoluta * 10_000) / anterior),
  };
}

// -----------------------------------------------------------------------------
// Somas
// -----------------------------------------------------------------------------

function somaOpcional(valores: readonly (number | null)[]): number | null {
  let soma = 0;
  let algum = false;
  for (const v of valores) {
    if (v !== null) {
      soma += v;
      algum = true;
    }
  }
  return algum ? soma : null;
}

export function somar(lista: readonly TemplatePerformance[]): TemplatePerformance {
  return {
    sent: lista.reduce((s, p) => s + p.sent, 0),
    delivered: lista.reduce((s, p) => s + p.delivered, 0),
    opened: somaOpcional(lista.map((p) => p.opened)),
    clicked: somaOpcional(lista.map((p) => p.clicked)),
    replied: lista.reduce((s, p) => s + p.replied, 0),
    paid: lista.reduce((s, p) => s + p.paid, 0),
    paidViaSeller: lista.reduce((s, p) => s + p.paidViaSeller, 0),
    recoveredCents: lista.reduce((s, p) => s + p.recoveredCents, 0),
  };
}

/** Soma do periodo anterior; `null` quando nenhum modelo tem base (janela "desde o início"). */
export function somarAnterior(lista: readonly MessageTemplate[]): TemplatePerformance | null {
  const anteriores = lista.map((t) => t.previousPerformance).filter((p): p is TemplatePerformance => p !== null);
  return anteriores.length === 0 && lista.length > 0 ? null : somar(anteriores);
}

// -----------------------------------------------------------------------------
// Ranking
// -----------------------------------------------------------------------------

export interface PosicaoNoGrupo {
  readonly posicao: number;
  readonly de: number;
  readonly rotuloGrupo: string;
  readonly melhor: boolean;
}

export type Ranking = ReadonlyMap<string, PosicaoNoGrupo>;

type ChaveGrupo = Pick<MessageTemplate, 'channel' | 'category' | 'audience' | 'touch'>;

export function chaveDoGrupo(t: ChaveGrupo): string {
  return `${t.channel}|${t.category}|${t.audience}|${t.touch ?? 'avulso'}`;
}

export function rotuloDoGrupo(t: ChaveGrupo): string {
  return `${CATEGORIA_LABEL[t.category]} · ${CANAL_CURTO[t.channel]} · ${PUBLICO_LABEL[t.audience]} ${t.touch ?? '(disparo avulso)'}`;
}

/** Publicos cujo pagamento e creditado ao toque (ADR-001): so eles competem pela taxa de pagamento. */
export function geraCredito(t: Pick<MessageTemplate, 'audience'>): boolean {
  return t.audience === 'checkout_abandonado' || t.audience === 'plano_vencido';
}

export function elegivelAoRanking(t: MessageTemplate): boolean {
  return geraCredito(t) && (t.status === 'aprovado' || t.status === 'texto_livre') && t.performance.sent >= AMOSTRA_MINIMA;
}

/** Ordem do ranking: taxa de pagamento, depois pagamentos, depois nome (estavel entre renders). */
export function compararDesempenho(a: MessageTemplate, b: MessageTemplate): number {
  const ta = taxaPagamentoBps(a.performance) ?? -1;
  const tb = taxaPagamentoBps(b.performance) ?? -1;
  if (ta !== tb) {
    return tb - ta;
  }
  if (a.performance.paid !== b.performance.paid) {
    return b.performance.paid - a.performance.paid;
  }
  return a.name.localeCompare(b.name);
}

export function ranquear(lista: readonly MessageTemplate[]): Ranking {
  const grupos = new Map<string, MessageTemplate[]>();
  for (const t of lista) {
    if (!elegivelAoRanking(t)) {
      continue;
    }
    const chave = chaveDoGrupo(t);
    const grupo = grupos.get(chave);
    if (grupo === undefined) {
      grupos.set(chave, [t]);
    } else {
      grupo.push(t);
    }
  }
  const ranking = new Map<string, PosicaoNoGrupo>();
  for (const grupo of grupos.values()) {
    if (grupo.length < 2) {
      continue;
    }
    const ordenado = [...grupo].sort(compararDesempenho);
    ordenado.forEach((t, i) => {
      ranking.set(t.id, { posicao: i + 1, de: ordenado.length, rotuloGrupo: rotuloDoGrupo(t), melhor: i === 0 });
    });
  }
  return ranking;
}

/** Por que um modelo nao tem posicao no ranking, em uma frase. */
export function motivoForaDoRanking(t: MessageTemplate): string {
  if (!geraCredito(t)) {
    return 'Fora do ranking: este público não gera crédito próprio, então não compete pela taxa de pagamento.';
  }
  if (t.status === 'pendente') {
    return 'Ainda não saiu: aguarda a aprovação da Meta.';
  }
  if (t.status === 'rejeitado') {
    return 'Não sai: recusado pela Meta.';
  }
  if (t.performance.sent < AMOSTRA_MINIMA) {
    return `Amostra pequena: menos de ${AMOSTRA_MINIMA} envios no período. Com poucos envios, a taxa é sorte, não resultado.`;
  }
  return `Único modelo de ${rotuloDoGrupo(t)} com envios no período: não há com quem comparar.`;
}
